<script setup>
import { onMounted, onUnmounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useI18n } from 'vue-i18n'
import router, { menuRoutesEnum, getRouterPathFromCacheKey } from '@/router'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'

import { useWebStreamWs } from '@/ws/web-stream.ws'
import { storeToRefs } from 'pinia'

import { getReferenceLinkText } from '@/services/entity-lock.service'

const i18n = getI18n()
const { t } = useI18n()
const webSiteStore = useWebSiteStore()
const { notifyInfo, notifyWarning, notifySuccess, notifyInfoWithLink } = useNotify()
const webStreamWs = useWebStreamWs()
const subscriptions = []
const { notificationsCount } = storeToRefs(webSiteStore)

const onClick = () => router.push(menuRoutesEnum.NOTIFICATIONS)

const markNotificationAsReadAsync = async(id) => {
  const data = [{id: id, is_read: true}]
  await webSiteStore.updateNotificationsAsReadAsync(data)
}

const onNotificationMessage = (sender, response, message) => {
  const counter = message.data.length
  if (counter > webSiteStore.notificationsCount) {
    notifyInfo(i18n.t('common.newNotification', counter), {
      timeout: window.config.NOTIFICATION_DURATION,
      actions: [{ icon: 'mdi-magnify', color: 'white', handler: onClick }]
    })
  }
  message.data.map(n => {
    if (n.type === webSiteStore.notificationTypesEnum.ENTITY_LOCK_REQUEST) {
      if (webSiteStore.currentlyLockedEntity?.lockedKey === n.internal_message?.cache_key) {
        notifyWarning(i18n.t('warning.entity_lock.unlock_request', {user: n.requested_by}), {
          timeout: 0,
          actions: [{ icon: 'mdi-close', color: 'white', round: true, handler: () => markNotificationAsReadAsync(n.id) }]
        })
        webSiteStore.startUserActivityTracking()
      }
    }
    if (n.type === webSiteStore.notificationTypesEnum.PRINT) {
      notifySuccess(i18n.t('common.printedReport'), {
        timeout: window.config.NOTIFICATION_DURATION,
        actions: [
          {
            label: i18n.t('common.download'), size: 'sm', color: 'white', handler: async() => {
              markNotificationAsReadAsync(n.id)
              await webSiteStore.api.printReport({ fileUrl: n.internal_message?.file_url, filename: n.internal_message?.filename })
            }
          }
        ]
      })
    }
    if (n.type === webSiteStore.notificationTypesEnum.ENTITY_LOCK_RELEASE_CONFIRMATION) {
      const data = {
        prefix: t(n.internal_message.reference_link_data.text)
      }

      n.internal_message.reference_link_data.identifier_params.forEach((param, i) => {
        data[param] = n.internal_message.reference_link_data.identifier_values[i]
      })

      notifyInfoWithLink(
        {
          data,
          text: getReferenceLinkText(n.internal_message),
          queryFields: [...n.internal_message.reference_link_data.identifier_params],
          target: getRouterPathFromCacheKey(n.internal_message)
        },
        'info.entity_lock.requested_entity_released_with_link',
        {
          timeout: 0,
          actions: [{ icon: 'mdi-close', color: 'white', round: true, handler: () => markNotificationAsReadAsync(n.id) }],
          group: getReferenceLinkText(n.internal_message)
        },
        t
      )
    }
  })
  webSiteStore.notificationsCount = counter
}

const subscribeNotificationStreamAsync = async() => {
  const subscription = await webStreamWs.subscribeAsync(
    [webStreamWs.NOTIFICATION_COUNT_STREAM],
    onNotificationMessage,
    onNotificationMessage,
  )
  subscriptions.push(subscription)
}

onMounted(async() => await subscribeNotificationStreamAsync())

onUnmounted(() => subscriptions.forEach((subscription) => subscription?.unsubscribe()))

</script>

<template>
  <q-btn
    flat
    round
    dense
    icon="mdi-bell"
    class="q-mr-sm q-ml-sm"
    @click="onClick"
  >
    <q-badge
      v-if="notificationsCount > 0"
      color="red"
      :label="notificationsCount"
      floating
      rounded
    />
  </q-btn>
</template>
